import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Description, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { countAnswers, HYGIENE_FACILITY_CHECKLIST, HYGIENE_SECTIONS } from '../lib/canadagap-checklists'
import { ChecklistFieldset, type ChecklistValue } from '../lib/checklist-fieldset'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useHygieneFacilityChecks, type HygieneFacilityCheckInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import { useLogNames } from '../lib/farm-log-names'
import { NameInput } from '../lib/name-input'
import type { Checklist, HygieneFacilityCheck } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Cleaning and Maintenance — Personal Hygiene Facilities'

function HygieneFacilityCheckDialog({
  onClose,
  initial,
  facilities,
  onSave,
}: {
  onClose: () => void
  initial?: HygieneFacilityCheck
  facilities: string[]
  onSave: (input: HygieneFacilityCheckInput) => Promise<unknown>
}) {
  const [facilityName, setFacilityName] = useState(initial?.facilityName ?? '')
  const [checkedOn, setCheckedOn] = useState(initial?.checkedOn ?? '')
  const [checkedAt, setCheckedAt] = useState(text(initial?.checkedAt))
  const [checklist, setChecklist] = useState<ChecklistValue>(initial?.checklist ?? {})
  const [assessmentOk, setAssessmentOk] = useState(initial?.assessmentOk ?? false)
  const [assessmentNotes, setAssessmentNotes] = useState(text(initial?.assessmentNotes))
  const [cleanedBy, setCleanedBy] = useState(text(initial?.cleanedBy))
  const [cleanedByCompany, setCleanedByCompany] = useState(initial?.cleanedByCompany ?? false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, { form: 'j', entry: {
      facilityName: orNull(facilityName),
      checkedOn: checkedOn || null,
      checkedAt: checkedAt || null,
      checklist,
      assessmentOk,
      assessmentNotes: orNull(assessmentNotes),
      cleanedBy: orNull(cleanedBy),
      cleanedByCompany,
    } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!facilityName.trim() || !checkedOn) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        facilityName: facilityName.trim(),
        checkedOn,
        checkedAt: checkedAt || null,
        checklist: checklist as Checklist,
        assessmentOk,
        assessmentNotes: orNull(assessmentNotes),
        cleanedBy: orNull(cleanedBy),
        cleanedByCompany,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save check.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} size="2xl">
      <DialogTitle>{initial ? 'Edit facility check' : 'Check a washroom or hand-washing facility'}</DialogTitle>
      <DialogDescription>
        At least weekly while in use, daily in peak season. Cleaning covers toilet, sink, floor, paper towel dispenser and all handles.
      </DialogDescription>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="J" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Type of facility and location</Label>
              <NameInput
                suggestions={facilities}
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="e.g. Portable toilet — north field"
                required
                autoFocus
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField>
                <Label>Date</Label>
                <Input type="date" value={checkedOn} onChange={(e) => setCheckedOn(e.target.value)} required />
              </FormField>
              <FormField>
                <Label>Time</Label>
                <Input type="time" value={checkedAt} onChange={(e) => setCheckedAt(e.target.value)} />
                <InputWarning result={check} input="checkedAt" />
              </FormField>
            </div>
          </FieldGroup>

          <div className="mt-8">
            <ChecklistFieldset sections={HYGIENE_SECTIONS} items={HYGIENE_FACILITY_CHECKLIST} value={checklist} onChange={setChecklist} />
          </div>

          <FieldGroup className="mt-8">
            <label className="flex items-start gap-3">
              <Checkbox checked={assessmentOk} onChange={setAssessmentOk} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                Assessment of facilities OK, or OK after corrective action (e.g. toilets pumped, supplies restocked)
              </span>
            </label>
            <FormField>
              <Label>Assessment notes</Label>
              <Textarea rows={2} value={assessmentNotes} onChange={(e) => setAssessmentNotes(e.target.value)} placeholder="What was found and what was done" />
            </FormField>
            <FormField>
              <Label>{cleanedByCompany ? 'Person confirming cleaning completed by a company' : 'Employee responsible for cleaning'}</Label>
              <Input value={cleanedBy} onChange={(e) => setCleanedBy(e.target.value)} />
              <Description>Confirms all cleaning was completed.</Description>
              <InputWarning result={check} input="cleanedBy" />
            </FormField>
            <label className="flex items-center gap-3">
              <Checkbox checked={cleanedByCompany} onChange={setCleanedByCompany} />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Cleaned by a company (e.g. a portable toilet service)</span>
            </label>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="J" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="J" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function HygieneFacilityChecksPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [facility, setFacility] = useState('')
  const [dialog, setDialog] = useState<DialogState<HygieneFacilityCheck>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useHygieneFacilityChecks(org?.id, farmId, year, facility || undefined)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)
  const { names, reload: reloadNames } = useLogNames(org?.id, farmId, 'facility')

  async function save(input: HygieneFacilityCheckInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await Promise.all([reloadStatus(), reloadNames()])
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => `${r.checkedOn} ${r.checkedAt ?? ''}`)).map((log) => {
    const counts = countAnswers(HYGIENE_FACILITY_CHECKLIST, log.checklist)
    return {
      key: log.id,
      date: log.checkedOn,
      onClick: () => setDialog({ log }),
      content: (
        <>
          <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
            {log.facilityName}
            {log.checkedAt && <span className="text-zinc-400 dark:text-zinc-500"> · {log.checkedAt}</span>}
          </p>
          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
            {log.assessmentOk === false ? (
              <span className="text-red-600 dark:text-red-400">Assessment not OK</span>
            ) : log.assessmentOk ? (
              'Assessment OK'
            ) : (
              'Assessment not recorded'
            )}
            {counts.issues > 0 && <> · {counts.issues} item{counts.issues > 1 ? 's' : ''} short</>}
            {log.cleanedBy && <> · {log.cleanedBy}</>}
          </p>
          <EntryIssues issue={issueFor(status?.forms.j, log.id)} />
        </>
      ),
    }
  })

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="j"
      title="Personal hygiene facilities"
      formName={FORM_NAME}
      addLabel="Check a facility"
      addHref={`/farms/${farmId}/log/hygiene`}
      year={year}
      onYearChange={setYear}
      nameFilter={{ label: 'Facility', options: names, value: facility, onChange: setFacility }}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No facility checks in ${year}.`}
      saved={saved}
    >
      {dialog && (
        <HygieneFacilityCheckDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} facilities={names} onSave={save} />
      )}
    </FarmLogLayout>
  )
}
